/**
 *  Cart drawer: accessibilità e pacchetto regalo.
 *
 *  Non modifica app.js: ne osserva il DOM e gli eventi.
 *  - app.js sostituisce l'innerHTML di <cart-drawer> a ogni aggiornamento AJAX, quindi
 *    gli ascoltatori sono delegati sul drawer (che resta) e mai sui suoi figli.
 *  - Focus trap con Tab/Shift+Tab mentre il drawer è aperto, ritorno del focus al
 *    pulsante del carrello alla chiusura, focus recuperato dopo un re-render.
 *  - Annuncio aria-live del riepilogo ([data-cart-summary]) dopo ogni re-render.
 *  - Chiusura da tastiera (Enter/Spazio) sul custom element side-panel-close.
 *  - Pulsante checkout disabilitato mentre una riga si aggiorna.
 *  - Pacchetto regalo: aggiunta/rimozione e refresh tramite l'evento "cart:refresh" del tema.
 *  - Codice sconto: applicazione/rimozione via /cart/update.js e messaggio se non applicabile.
 */
(() => {
  const drawer = document.getElementById('Cart-Drawer');
  if (!drawer || drawer.dataset.a11yReady) {
    return;
  }
  drawer.dataset.a11yReady = 'true';

  const toggle = document.getElementById('cart-drawer-toggle');
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // Regione live fuori dal drawer: il suo contenuto non viene mai sostituito dal re-render
  const live = document.createElement('div');
  live.className = 'visually-hidden';
  live.setAttribute('aria-live', 'polite');
  live.setAttribute('aria-atomic', 'true');
  document.body.appendChild(live);

  const isOpen = () => drawer.classList.contains('active');
  const summaryText = () => drawer.querySelector('[data-cart-summary]')?.textContent.trim() || '';

  // Elementi raggiungibili e visibili (il pannello note chiuso e l'upsell nascosto restano fuori)
  const focusables = () => [...drawer.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === document.activeElement);

  /* --- Focus trap e chiusura da tastiera --- */
  drawer.addEventListener('keydown', (event) => {
    const close = event.target.closest('side-panel-close');
    if (close && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      close.click();
      return;
    }

    if (event.key !== 'Tab' || !isOpen()) {
      return;
    }
    const items = focusables();
    if (!items.length) {
      event.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === drawer)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* --- Apertura/chiusura: stato del toggle e ritorno del focus --- */
  let wasOpen = isOpen();
  new MutationObserver(() => {
    const open = isOpen();
    if (open === wasOpen) {
      return;
    }
    wasOpen = open;
    toggle?.setAttribute('aria-expanded', String(open));
    if (!open && drawer.contains(document.activeElement)) {
      toggle?.focus();
    }
  }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  toggle?.setAttribute('aria-controls', 'Cart-Drawer');
  toggle?.setAttribute('aria-expanded', String(isOpen()));

  /* --- Re-render AJAX: annuncio e recupero del focus --- */
  let lastSummary = summaryText();
  // Riferimento al controllo attivo prima del re-render, per ritrovarne l'equivalente dopo
  let focusKey = null;
  drawer.addEventListener('focusin', (event) => {
    const el = event.target;
    const item = el.closest('[id^="CartDrawerItem-"]');
    if (item) {
      const role = ['minus', 'plus', 'qty', 'remove'].find((c) => el.classList.contains(c));
      focusKey = role ? `#${item.id} .${role}` : null;
    } else {
      focusKey = el.id ? `#${CSS.escape(el.id)}` : null;
    }
  });

  new MutationObserver(() => {
    // Il re-render sostituisce i figli diretti di <cart-drawer>
    const summary = summaryText();
    if (summary && summary !== lastSummary && isOpen()) {
      // Svuota e riscrive nel frame successivo: alcuni screen reader ignorano testo identico
      live.textContent = '';
      requestAnimationFrame(() => {
        live.textContent = summary;
      });
    }
    lastSummary = summary;

    restoreDiscount();
    restoreScroll();

    if (isOpen() && (document.activeElement === document.body || !document.activeElement)) {
      const target = (focusKey && drawer.querySelector(focusKey)) || drawer.querySelector('.side-panel-close');
      (target || drawer).focus({ preventScroll: true });
    }
  }).observe(drawer, { childList: true });

  /* --- Posizione di scorrimento ---
     Il re-render ricrea il contenitore che scorre (.side-panel-inner su mobile,
     .side-panel-content altrove) e lo riporterebbe in cima: si ricorda l'ultima
     posizione e la si riapplica. L'evento scroll non risale: serve la fase di cattura. */
  const SCROLLERS = ['side-panel-inner', 'side-panel-content'];
  let scrollState = null;
  drawer.addEventListener(
    'scroll',
    (event) => {
      const name = SCROLLERS.find((c) => event.target.classList?.contains(c));
      if (name) {
        scrollState = { selector: `.${name}`, top: event.target.scrollTop };
      }
    },
    true
  );

  // Chiamata dopo ogni re-render del drawer (vedi MutationObserver sopra)
  function restoreScroll() {
    const scroller = scrollState && drawer.querySelector(scrollState.selector);
    if (scroller) {
      scroller.scrollTop = scrollState.top;
    }
  }

  /* --- Checkout disabilitato durante l'aggiornamento di una riga --- */
  let busyTimer = null;
  const setBusy = (busy) => {
    drawer.querySelector('.side-panel-inner')?.setAttribute('aria-busy', String(busy));
    const checkout = drawer.querySelector('.checkout-button');
    if (checkout) {
      checkout.disabled = busy;
      checkout.classList.toggle('loading', busy);
    }
    clearTimeout(busyTimer);
    // Rete di sicurezza: se la richiesta fallisce app.js non emette l'evento di fine
    if (busy) {
      busyTimer = setTimeout(() => setBusy(false), 8000);
    }
  };
  document.addEventListener('line-item:change:start', () => setBusy(true));
  document.addEventListener('line-item:change:end', () => setBusy(false));

  /* --- Codice sconto ---
     Il parametro "discount" di /cart/update.js sostituisce TUTTI i codici del carrello:
     si invia sempre l'elenco completo (codici già applicati + nuovo, oppure senza quello
     rimosso). Stato del pannello ed errore sopravvivono al re-render tramite queste variabili. */
  let discountOpen = false;
  let discountError = '';
  // Testo digitato: dopo un errore resta nel campo per poterlo correggere
  let discountValue = '';

  const updateCart = (body) =>
    fetch(`${theme.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`cart/update ${response.status}`);
      }
      return response.json();
    });

  const appliedCodes = () => [...drawer.querySelectorAll('.cart-discount__chip')].map((chip) => chip.dataset.code);

  function setDiscountOpen(open) {
    discountOpen = open;
    const toggleButton = drawer.querySelector('.cart-discount__toggle');
    const panel = drawer.querySelector('.cart-discount__panel');
    if (!toggleButton || !panel) {
      return;
    }
    toggleButton.setAttribute('aria-expanded', String(open));
    panel.hidden = !open;
  }

  function showDiscountError(message) {
    discountError = message;
    const error = drawer.querySelector('.cart-discount__error');
    const input = drawer.querySelector('.cart-discount__input');
    if (error) {
      error.textContent = message;
      error.hidden = !message;
    }
    input?.setAttribute('aria-invalid', String(Boolean(message)));
  }

  // Chiamata dopo ogni re-render del drawer (vedi MutationObserver sopra)
  function restoreDiscount() {
    setDiscountOpen(discountOpen);
    showDiscountError(discountError);
    const input = drawer.querySelector('.cart-discount__input');
    if (input && discountError) {
      input.value = discountValue;
    }
  }

  function refreshAfterDiscount(nextFocus) {
    // Il re-render distrugge il controllo attivo: si indica dove riportare il focus
    focusKey = nextFocus;
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  drawer.addEventListener('click', (event) => {
    if (event.target.closest('.cart-discount__toggle')) {
      setDiscountOpen(!discountOpen);
      if (discountOpen) {
        drawer.querySelector('.cart-discount__input')?.focus();
      }
      return;
    }

    const remove = event.target.closest('.cart-discount__remove');
    if (!remove) {
      return;
    }
    const remaining = appliedCodes().filter((code) => code !== remove.dataset.code);
    remove.disabled = true;
    setBusy(true);
    showDiscountError('');
    updateCart({ discount: remaining.join(',') })
      .catch((error) => console.error(error))
      .finally(() => {
        setBusy(false);
        refreshAfterDiscount('.cart-discount__toggle');
      });
  });

  drawer.addEventListener('submit', (event) => {
    const form = event.target.closest('.cart-discount__form');
    if (!form) {
      return;
    }
    event.preventDefault();

    const box = form.closest('[data-cart-discount]');
    const input = form.querySelector('.cart-discount__input');
    const apply = form.querySelector('.cart-discount__apply');
    const code = input.value.trim();
    discountValue = code;
    if (!code) {
      showDiscountError(box.dataset.msgEmpty);
      input.focus();
      return;
    }

    const current = appliedCodes();
    if (current.some((c) => c.toLowerCase() === code.toLowerCase())) {
      // Già applicato: niente richiesta, si svuota il campo
      input.value = '';
      showDiscountError('');
      return;
    }

    apply.disabled = true;
    apply.classList.add('loading');
    setBusy(true);
    showDiscountError('');

    updateCart({ discount: [...current, code].join(',') })
      .then((cart) => {
        const result = (cart.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
        if (result && result.applicable) {
          // Applicato: il codice compare come chip, il pannello si richiude
          discountOpen = false;
          return;
        }
        // Non applicabile (o sconosciuto): si mostra il motivo e si toglie il codice dal carrello
        showDiscountError(box.dataset.msgNotApplicable.replace('__CODE__', code.toUpperCase()));
        return updateCart({ discount: current.join(',') });
      })
      .catch((error) => {
        console.error(error);
        showDiscountError(box.dataset.msgError);
      })
      .finally(() => {
        setBusy(false);
        // Con errore il pannello resta aperto e il focus torna nel campo
        refreshAfterDiscount(discountError ? '#CartDiscountInput' : '.cart-discount__toggle');
      });
  });

  /* --- Pacchetto regalo --- */
  drawer.addEventListener('change', (event) => {
    const checkbox = event.target.closest('.gift-wrap-checkbox');
    if (!checkbox) {
      return;
    }
    const box = checkbox.closest('.gift-wrap-box');
    checkbox.disabled = true;
    box?.classList.add('loading');
    setBusy(true);

    fetch(`${theme.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: { [checkbox.dataset.variantId]: checkbox.checked ? 1 : 0 } })
    })
      .catch((error) => console.error(error))
      .finally(() => {
        // Il tema ri-renderizza drawer e contatore (sezioni cart-drawer e cart-bubble)
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        setBusy(false);
      });
  });
})();
